import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';

interface GuardProps {
  children: ReactNode;
}

/** Redirects to / unless the caller has WICnic.Admin. */
export function AdminGuard({ children }: GuardProps) {
  const { isSignedIn, isAdmin, authConfigured } = useAuth();
  if (!authConfigured) return <NotConfigured />;
  if (!isSignedIn) return <Navigate to="/" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Redirects to / unless the caller has Editor or Admin. */
export function EditorGuard({ children }: GuardProps) {
  const { isSignedIn, isEditor, authConfigured } = useAuth();
  if (!authConfigured) return <NotConfigured />;
  if (!isSignedIn) return <Navigate to="/" replace />;
  if (!isEditor) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Renders children only for Admins (UX hiding — backend still enforces). */
export function AdminOnly({ children }: GuardProps) {
  const { isAdmin } = useAuth();
  if (!isAdmin) return null;
  return <>{children}</>;
}

/** Renders children only for Editors and Admins (UX hiding). */
export function EditorOnly({ children }: GuardProps) {
  const { isEditor } = useAuth();
  if (!isEditor) return null;
  return <>{children}</>;
}

function NotConfigured() {
  return (
    <div style={{ padding: '4rem 1.25rem', maxWidth: 720, margin: '0 auto' }}>
      <span className="eyebrow">Auth Not Configured</span>
      <h2 style={{ marginBottom: '1rem' }}>Sign-in unavailable</h2>
      <p style={{ color: 'var(--mid)' }}>
        The Entra app registration hasn't been created yet, so signed-in pages
        aren't reachable. The public side of the site still works — head back
        to the home page.
      </p>
    </div>
  );
}
